/**
 * Admin V3 — Onboarding module — top-level page
 *
 * Mounted at: /admin-v3/onboarding (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Pending Driver Approvals View (read-only).
 * NOT a complete onboarding subsystem. This phase is intentionally a thin
 * read-only window into the GET /admin/beta/pending-drivers queue. A future
 * Phase 3+ workstream may refactor this area into a true onboarding domain
 * (recruitment, KYC, document collection, contracts, training, financial
 * setup, activation) — out of Phase 2.4 scope.
 *
 * SCOPE FOR PHASE 2.4:
 *   - Read-only: GET /admin/beta/pending-drivers (RBAC: ADMIN_BETA_PENDING)
 *   - Endpoint hard-coded server-side to filter approval_status="pending"
 *     and to_list(100). No query params, no pagination, no server-side search.
 *   - Client-side search over the loaded page (name/email/phone/plate)
 *   - Per-row click → /admin-v3/onboarding/:driverId (placeholder for now)
 *   - NO CSV export (backend exposes none)
 *   - NO status filter (endpoint is fixed-filter)
 *   - NO pagination (endpoint is non-paginated)
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No POST/PATCH/PUT/DELETE; no approve/reject controls; no mutation hooks
 *   - No legacy /admin Beta tab handoff link (owner ruling)
 *
 * Uses V3 foundation hook useAdminQuery (auth-memoized, abort-on-unmount).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { UserPlus } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import OnboardingListSearchBar from './OnboardingListSearchBar';
import OnboardingListTable from './OnboardingListTable';
import BetaStatusCard from './BetaStatusCard';

const SEARCH_DEBOUNCE_MS = 300;
const BACKEND_HARD_CAP = 100;  // backend to_list(100) — admin.py:476

const OnboardingListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  // GET /admin/beta/pending-drivers — endpoint accepts no query params.
  const { data, loading, error, refetch } = useAdminQuery('/admin/beta/pending-drivers');
  const candidatesRaw = Array.isArray(data) ? data : [];

  // Client-side search over the loaded page (case-insensitive, multi-field)
  const candidates = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return candidatesRaw;
    return candidatesRaw.filter((d) =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').toLowerCase().includes(q) ||
      (d.license_plate || '').toLowerCase().includes(q)
    );
  }, [candidatesRaw, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch);
  const isAtBackendCap = candidatesRaw.length === BACKEND_HARD_CAP;

  return (
    <div className="space-y-6">
      {/* AV3-34 / P1-B3-6: read-only beta status card. Self-contained (own query
          + RBAC gate); does not interact with the pending-drivers workflow below. */}
      <BetaStatusCard locale={locale} />

      <SectionCard
        title={t('onboarding.title', locale)}
        icon={UserPlus}
        iconClass="bg-amber-100 text-amber-600"
      >
        <div className="space-y-4">
        <OnboardingListSearchBar
          search={search}
          onSearchChange={setSearch}
          resultsCount={candidates.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <OnboardingListTable
          candidates={candidates}
          loading={loading}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {/* 100-cap governance hint (per backend admin.py:476 to_list(100)) */}
        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-onboarding-cap-hint"
          >
            {t('onboarding.cap.hint', locale)}
          </p>
        )}
        </div>
      </SectionCard>
    </div>
  );
};

export default OnboardingListPage;
